import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building, Mail, Lock, ArrowRight, Car, Home, CalendarCheck, MapPin } from 'lucide-react';
import apiClient from '../../services/api';
import './Login.css';

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();

    const getLoginErrorMessage = (error) => {
        const status = error?.response?.status;
        const identifier = email?.trim() || 'This user';

        if (status === 401) {
            return `${identifier} is correct, but the password is wrong.`;
        }

        if (status === 404) {
            return `${identifier} was not found. Please check the username/email.`;
        }

        return error?.response?.data?.message || 'Login failed. Please check your credentials.';
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        
        try {
            const response = await apiClient.post('/auth/login', { email, password });
            
            // Assuming response contains { token, user }
            const token = response.data.token || response.data.accessToken;
            if (token) {
                localStorage.setItem('token', token);
            }
            localStorage.setItem('user', JSON.stringify(response.data.user || response.data));

            // Fetch entity data immediately to prevent flickering in Sidebar/Dashboard
            try {
                const entityRes = await apiClient.get('/users/me/entity');
                if (entityRes.data?.data) {
                    localStorage.setItem('entity', JSON.stringify(entityRes.data.data));
                }
            } catch (e) {
                console.warn('Could not fetch entity during login:', e);
            }

            setIsLoading(false);
            navigate('/');
        } catch (error) {
            setIsLoading(false);
            alert(getLoginErrorMessage(error));
            console.error('Login Error:', error);
        }
    };

    return (
        <div className="login-container">
            {/* ─── Left Panel (Form) ─── */}
            <div className="login-left-panel">
                <div className="login-form-box">
                    <div className="login-header">
                        <h2>Welcome back</h2>
                        <p>Enter your details to access your owner portal.</p>
                    </div>

                    <form onSubmit={handleLogin} className="login-form">
                        <div className="input-group">
                            <label>Username / Email Address</label>
                            <div className="input-icon-wrapper">
                                <Mail size={18} className="input-icon" />
                                <input 
                                    type="text" 
                                    placeholder="admin@example.com" 
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                />
                            </div>
                        </div>

                        <div className="input-group">
                            <label>Password</label>
                            <div className="input-icon-wrapper">
                                <Lock size={18} className="input-icon" />
                                <input 
                                    type="password" 
                                    placeholder="••••••••" 
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                />
                            </div>
                            <div className="forgot-password">
                                <a href="#forgot">Forgot password?</a>
                            </div>
                        </div>

                        <div className="remember-me">
                            <input type="checkbox" id="remember" />
                            <label htmlFor="remember">Remember me for 30 days</label>
                        </div>

                        <button 
                            type="submit" 
                            className={`login-btn ${isLoading ? 'loading' : ''}`}
                            disabled={isLoading}
                        >
                            {isLoading ? 'Signing in...' : 'Sign In'}
                            {!isLoading && <ArrowRight size={18} />}
                        </button>
                    </form>

                    <div className="login-footer">
                        <p>Don't have an account? <a href="#contact">Contact Support</a></p>
                    </div>
                </div>
            </div>

            {/* ─── Right Panel (Branding & Animations) ─── */}
            <div className="login-right-panel">
                <div className="login-illustration">
                    {/* Animated Cards floating in the background */}
                    <div className="animated-card anim-hotel">
                        <div className="animated-icon"><Building size={20} color="white" /></div>
                        <div className="animated-details">
                            <h4>Jees Hotel Suite</h4>
                            <p>Customer Arrival: 2:00 PM</p>
                        </div>
                    </div>
                    
                    <div className="animated-card anim-car">
                        <div className="animated-icon"><Car size={20} color="white" /></div>
                        <div className="animated-details">
                            <h4>Luxury SUV Rental</h4>
                            <p>Booked • 3 Days Left</p>
                        </div>
                    </div>

                    <div className="animated-card anim-property">
                        <div className="animated-icon"><Home size={20} color="white" /></div>
                        <div className="animated-details">
                            <h4>Beachfront Villa</h4>
                            <p>Cleaned & Ready</p>
                        </div>
                    </div>
                    
                    <div className="animated-card anim-user">
                        <div className="animated-icon"><CalendarCheck size={20} color="white" /></div>
                        <div className="animated-details">
                            <h4>New Reservation</h4>
                            <p>+$350 confirmed</p>
                        </div>
                    </div>

                    {/* Background abstract shapes */}
                    <div className="abstract-shape shape-1"></div>
                    <div className="abstract-shape shape-2"></div>
                </div>

                <div className="login-branding">
                    <div className="login-logo-container">
                        <MapPin size={36} color="white" />
                    </div>
                    <h1>Somstay Partner Portal</h1>
                    <p>
                        Manage your hotels, properties, and cars in one dedicated hub. 
                        Track all your customer orders, arrival times, and operations in real-time.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Login;
